/**
 * Appointment Calendar Module
 * Handles FullCalendar initialization and date/time selection for appointments
 */

import { Calendar } from '@fullcalendar/core';
import interactionPlugin from '@fullcalendar/interaction';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import multiMonthPlugin from '@fullcalendar/multimonth';

import { createConfigParserFromElement } from './config.js';

export default class AppointmentCalendar {
    constructor() {
        this.calendar = null;
        this.calendarEl = null;
        this.configParser = null;
        this.bookings = [];
        this.bookingUrl = null;
        this.bookingsIndex = {};

        this.init();
    }

    /**
     * Initialize the calendar
     */
    init() {
        this.calendarEl = document.getElementById('fullCalendarAppointment');
        if (!this.calendarEl) {
            return;
        }

        // Get booking URL
        this.bookingUrl = this.calendarEl.dataset.booking;

        // Parse configuration
        this.configParser = createConfigParserFromElement('fullCalendarAppointment');
        if (!this.configParser) {
            return;
        }

        this.initializeCalendar();
    }

    /**
     * Initialize FullCalendar instance
     */
    initializeCalendar() {
        const config = this.configParser.getConfig();
        const curr = new Date();

        // Get minimum booking date from config
        const minBookingDate = this.configParser.getMinBookingDate();
        const startDate = `${minBookingDate.getFullYear()}-${(minBookingDate.getMonth() + 1).toString().padStart(2, '0')}-${minBookingDate.getDate().toString().padStart(2, '0')}`;

        this.calendar = new Calendar(this.calendarEl, {
            plugins: [interactionPlugin, dayGridPlugin, timeGridPlugin, listPlugin, multiMonthPlugin],

            timeZone: 'Europe/Luxembourg',

            headerToolbar: false,
            selectable: true,
            selectConstraint: { start: startDate, end: '3000-12-31' },
            selectOverlap: false,
            eventDurationEditable: false,
            eventStartEditable: false,
            navLinks: false,
            editable: false,
            dayMaxEvents: true,
            locale: 'fr',
            longPressDelay: 250,

            initialView: this.getInitialView(),

            views: {
                multiMonthOne: {
                    type: 'multiMonth',
                    duration: { months: 1 },
                    multiMonthMaxColumns: 1,
                    multiMonthMinWidth: 300,
                },
                multiMonthTwo: {
                    type: 'multiMonth',
                    duration: { months: 2 },
                    multiMonthMaxColumns: 2,
                    multiMonthMinWidth: 300,
                }
            },

            height: 'auto',
            contentHeight: 'auto',
            firstDay: 1,

            dayCellClassNames: (arg) => this.getDayCellClasses(arg, curr),

            datesSet: (dateInfo) => this.handleMonthChange(dateInfo),

            select: (selectionInfo) => this.handleDateSelection(selectionInfo),
        });

        this.setupEventListeners();
        this.calendar.render();
        this.updateNavigationButtons();

        // Fetch initial bookings for displayed months
        this.fetchInitialBookings();
    }

    /**
     * Get initial calendar view based on screen size
     */
    getInitialView() {
        return window.innerWidth >= 1200 ? 'multiMonthTwo' : 'multiMonthOne';
    }

    /**
     * Get view for resized window
     */
    getResizedView() {
        return window.innerWidth >= 1200 ? 'multiMonthTwo' : 'multiMonthOne';
    }

    /**
     * Get CSS classes for day cells based on configuration
     */
    getDayCellClasses(arg, curr) {
        const classes = [];
        const cellDate = new Date(arg.date);
        cellDate.setHours(0, 0, 0, 0);

        // Check if before minimum booking date
        const minBookingDate = this.configParser.getMinBookingDate();
        minBookingDate.setHours(0, 0, 0, 0);

        if (cellDate.getTime() < minBookingDate.getTime()) {
            classes.push('fc-day-not-selectable');
        }

        // Check if date is excluded
        if (this.configParser.isDateExcluded(cellDate)) {
            classes.push('fc-day-excluded');
            classes.push('fc-day-not-selectable');
        }

        // Check if day of week is open
        const dayOfWeek = cellDate.getDay();
        if (!this.configParser.isDayOpen(dayOfWeek)) {
            classes.push('fc-day-closed');
            classes.push('fc-day-not-selectable');
        }

        return classes;
    }

    /**
     * Handle date selection
     */
    handleDateSelection(selectionInfo) {
        if (!selectionInfo.jsEvent) {
            return;
        }

        const selectedDate = new Date(selectionInfo.start);
        selectedDate.setHours(0, 0, 0, 0);

        // Validate the selected date
        if (!this.isDateSelectable(selectedDate)) {
            // Unselect in calendar
            if (this.calendar) {
                this.calendar.unselect();
            }

            return;
        }

        const startDateTime = selectionInfo.start.toISOString().slice(0, 16);
        const endDateTime = selectionInfo.end.toISOString().slice(0, 16);

        // Store in sessionStorage only
        sessionStorage.setItem('appointmentStartDate', startDateTime);
        sessionStorage.setItem('appointmentEndDate', endDateTime);

        this.generateHourButtons(selectionInfo.start);

        document.dispatchEvent(new CustomEvent('appointmentDateSelected', {
            detail: {
                start: selectionInfo.start,
                end: selectionInfo.end,
                startDateTime: startDateTime,
                endDateTime: endDateTime
            }
        }));
    }

    /**
     * Check if a date is selectable
     */
    isDateSelectable(date) {
        const checkDate = new Date(date);
        checkDate.setHours(0, 0, 0, 0);

        // Check if before minimum booking date
        const minBookingDate = this.configParser.getMinBookingDate();
        minBookingDate.setHours(0, 0, 0, 0);

        if (checkDate.getTime() < minBookingDate.getTime()) {
            return false;
        }

        // Check if date is excluded
        if (this.configParser.isDateExcluded(checkDate)) {
            return false;
        }

        // Check if day of week is open
        const dayOfWeek = checkDate.getDay();
        if (!this.configParser.isDayOpen(dayOfWeek)) {
            return false;
        }

        return true;
    }

    /**
     * Generate hour buttons for selected date based on configuration
     */
    generateHourButtons(selectedDate) {
        const existingContainer = document.querySelector('.hour-buttons-dynamic');
        if (existingContainer) {
            const existingDate = existingContainer.dataset.selectedDate;
            const newDateStr = selectedDate.toDateString();
            if (existingDate === newDateStr) {
                return;
            }
        }

        document.querySelectorAll('.hour-buttons-dynamic').forEach(container => {
            container.remove();
        });

        const selectedDateStr = selectedDate.toISOString().split('T')[0];
        const dayElement = document.querySelector(`[data-date="${selectedDateStr}"]`);
        if (!dayElement) {
            return;
        }

        const monthContainer = dayElement.closest('.fc-multimonth-month');
        if (!monthContainer) {
            return;
        }

        // Generate time slots based on configuration
        const timeSlots = this.configParser.generateTimeSlotsForDate(selectedDate);

        if (timeSlots.length === 0) {
            return;
        }

        const hourButtonsHtml = `
            <div class="hour-buttons-dynamic" data-selected-date="${selectedDate.toDateString()}">
                <div class="row g-5px">
                    ${timeSlots.map(slot => {
                        // Check if this time slot is already booked
                        const isBooked = this.isTimeSlotBooked(selectedDate, slot.time);
                        const buttonClass = isBooked ? 'btn-hour w-100 booked' : 'btn-hour w-100 available shineHover';
                        const disabledAttr = isBooked ? 'disabled' : '';

                        return `
                            <div class="col-6 col-md-4 col-lg-3 mb-10 greenButtonCol">
                                <button type="button" class="${buttonClass}" data-hour="${slot.time}" ${disabledAttr}>
                                    ${slot.time}
                                </button>
                            </div>
                        `;
                    }).join('')}
                </div>
            </div>
        `;

        const daygrid = monthContainer.querySelector('.fc-daygrid');
        if (daygrid) {
            daygrid.insertAdjacentHTML('afterend', hourButtonsHtml);
        } else {
            monthContainer.insertAdjacentHTML('beforeend', hourButtonsHtml);
        }

        monthContainer.querySelectorAll('.btn-hour').forEach(btn => {
            btn.addEventListener('click', (e) => {
                if (btn.disabled || btn.classList.contains('booked')) return;

                document.querySelectorAll('.btn-hour.selected').forEach(selectedBtn => {
                    selectedBtn.classList.remove('selected');
                });

                btn.classList.add('selected');
                this.updateFormFields(btn.dataset.hour);
            });
        });
    }

    /**
     * Update form fields with selected hour
     */
    updateFormFields(selectedHour) {
        const form = document.querySelector('form[action*="appointment"], form[method="post"]');
        if (!form) return;

        const config = this.configParser.getConfig();
        const slotDuration = config.slotDuration;

        const selectedStartDate = sessionStorage.getItem('appointmentStartDate');
        const selectedEndDate = sessionStorage.getItem('appointmentEndDate');

        if (selectedStartDate && selectedEndDate) {
            const dateOnly = selectedStartDate.split('T')[0];
            const [hours, minutes] = selectedHour.split(':');

            // Calculate end time based on slot duration
            const endTime = this.calculateEndTime(hours, minutes, slotDuration);

            const newStartDateTime = `${dateOnly}T${selectedHour}:00`;
            const newEndDateTime = `${dateOnly}T${endTime}:00`;

            // Store in sessionStorage only
            sessionStorage.setItem('appointmentStartDate', newStartDateTime);
            sessionStorage.setItem('appointmentEndDate', newEndDateTime);

            const startDate = new Date(newStartDateTime);
            const formattedTime = startDate.toLocaleTimeString('fr-FR', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            });

            const timeElements = document.querySelectorAll('.js-appointment-time, .js-appointment-time-confirm, .js-appointment-time-final');
            timeElements.forEach(el => el.textContent = formattedTime);
        }

        // Update form input fields
        this.updateFormInputFields(selectedHour, slotDuration);

        // Enable booking button
        const bookingBtn = document.querySelector('#bookingSubmitBtn');
        if (bookingBtn) {
            bookingBtn.disabled = false;
        }
    }

    /**
     * Calculate end time based on start time and duration
     */
    calculateEndTime(hours, minutes, durationMinutes) {
        const startMinutes = parseInt(hours) * 60 + parseInt(minutes);
        const endMinutes = startMinutes + durationMinutes;
        const endHour = Math.floor(endMinutes / 60).toString().padStart(2, '0');
        const endMin = (endMinutes % 60).toString().padStart(2, '0');
        return `${endHour}:${endMin}`;
    }

    /**
     * Update form input fields
     */
    updateFormInputFields(selectedHour, slotDuration) {
        const form = document.querySelector('form[action*="appointment"], form[method="post"]');
        if (!form) return;

        let startTimeField = form.querySelector('input[name*="startTime"], input[name*="time_debut"], input[name="eventStartDate"]');
        let endTimeField = form.querySelector('input[name*="endTime"], input[name*="time_fin"], input[name="eventEndDate"]');

        if (startTimeField && startTimeField.name === 'eventStartDate') {
            const selectedDate = sessionStorage.getItem('appointmentStartDate');
            if (selectedDate) {
                const dateOnly = selectedDate.split('T')[0];
                startTimeField.value = `${dateOnly}T${selectedHour}`;
                startTimeField.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }

        if (endTimeField && endTimeField.name === 'eventEndDate') {
            const selectedDate = sessionStorage.getItem('appointmentEndDate');
            if (selectedDate) {
                const dateOnly = selectedDate.split('T')[0];
                const [hours, minutes] = selectedHour.split(':');
                const endTime = this.calculateEndTime(hours, minutes, slotDuration);

                endTimeField.value = `${dateOnly}T${endTime}`;
                endTimeField.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }
    }

    /**
     * Setup event listeners
     */
    setupEventListeners() {
        window.addEventListener('resize', () => {
            if (this.calendar) {
                this.calendar.changeView(this.getResizedView());
                this.updateNavigationButtons();
            }
        });

        setTimeout(() => {
            this.updateNavigationButtons();
        }, 100);
    }

    /**
     * Update navigation buttons
     */
    updateNavigationButtons() {
        document.querySelectorAll('#fullCalendarAppointment .custom-nav-buttons').forEach(btn => btn.remove());

        if (!this.calendarEl) return;

        const multiMonthContainer = this.calendarEl.querySelector('.fc-multimonth');
        if (!multiMonthContainer) return;

        const prevButton = this.createNavigationButton('prev');
        const nextButton = this.createNavigationButton('next');

        multiMonthContainer.style.position = 'relative';
        multiMonthContainer.appendChild(prevButton);
        multiMonthContainer.appendChild(nextButton);
    }

    /**
     * Create navigation button
     */
    createNavigationButton(direction) {
        const button = document.createElement('button');
        button.className = `custom-nav-buttons btn-nav-${direction}`;
        button.innerHTML = direction === 'prev'
            ? '<i class="fa-solid fa-chevron-left"></i>'
            : '<i class="fa-solid fa-chevron-right"></i>';

        const position = direction === 'prev' ? 'left: 20px' : 'right: 20px';
        button.style.cssText = `position: absolute; ${position}; top: 50%; transform: translateY(-50%); z-index: 10; background: white; border: 1px solid #ddd; border-radius: 50%; width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; cursor: pointer; box-shadow: 0 2px 4px rgba(0,0,0,0.1);`;

        button.addEventListener('click', () => {
            if (direction === 'prev') {
                this.calendar.prev();
            } else {
                this.calendar.next();
            }
            setTimeout(() => {
                this.updateNavigationButtons();
            }, 50);
        });

        return button;
    }

    /**
     * Fetch initial bookings for the displayed calendar range
     */
    async fetchInitialBookings() {
        if (!this.calendar) return;

        const view = this.calendar.view;
        const startDate = view.activeStart;
        const endDate = view.activeEnd;

        await this.fetchBookings(startDate, endDate);
    }

    /**
     * Handle month change in calendar
     */
    async handleMonthChange(dateInfo) {
        const startDate = dateInfo.start;
        const endDate = dateInfo.end;

        await this.fetchBookings(startDate, endDate);

        // Update navigation buttons
        this.updateNavigationButtons();
    }

    /**
     * Fetch bookings from API for a given date range
     */
    async fetchBookings(startDate, endDate) {
        if (!this.bookingUrl) {
            return;
        }

        try {
            const startDateStr = this.formatDateToYYYYMMDD(startDate);
            const endDateStr = this.formatDateToYYYYMMDD(endDate);

            const url = `${this.bookingUrl}?start=${startDateStr}&end=${endDateStr}`;

            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            // Store bookings
            this.bookings = data;

            // Create index by date for faster lookup
            this.createBookingsIndex();
        } catch (error) {
            this.bookings = [];
            this.bookingsIndex = {};
        }
    }

    /**
     * Create an index of bookings by date for faster lookup
     */
    createBookingsIndex() {
        this.bookingsIndex = {};

        this.bookings.forEach(booking => {
            const startDate = new Date(booking.start);
            const endDate = new Date(booking.end);

            // Get all dates that this booking spans
            const currentDate = new Date(startDate);
            while (currentDate <= endDate) {
                const dateStr = this.formatDateToYYYYMMDD(currentDate);

                if (!this.bookingsIndex[dateStr]) {
                    this.bookingsIndex[dateStr] = [];
                }

                this.bookingsIndex[dateStr].push({
                    id: booking.id,
                    start: startDate.toTimeString().slice(0, 5), // HH:MM
                    end: endDate.toTimeString().slice(0, 5), // HH:MM
                    startDateTime: startDate,
                    endDateTime: endDate
                });

                currentDate.setDate(currentDate.getDate() + 1);
            }
        });
    }

    /**
     * Check if a time slot is already booked
     */
    isTimeSlotBooked(date, time) {
        const dateStr = this.formatDateToYYYYMMDD(date);
        const bookingsForDate = this.bookingsIndex[dateStr];

        if (!bookingsForDate || bookingsForDate.length === 0) {
            return false;
        }

        // Get slot duration from config
        const config = this.configParser.getConfig();
        const slotDuration = config.slotDuration;

        // Parse the time slot
        const [hours, minutes] = time.split(':').map(Number);
        const slotStartMinutes = hours * 60 + minutes;
        const slotEndMinutes = slotStartMinutes + slotDuration;

        // Check if any booking overlaps with this time slot
        return bookingsForDate.some(booking => {
            const bookingStart = booking.startDateTime;
            const bookingEnd = booking.endDateTime;

            // Create datetime objects for the slot
            const slotStart = new Date(date);
            slotStart.setHours(Math.floor(slotStartMinutes / 60), slotStartMinutes % 60, 0, 0);

            const slotEnd = new Date(date);
            slotEnd.setHours(Math.floor(slotEndMinutes / 60), slotEndMinutes % 60, 0, 0);

            // Check for overlap: slot overlaps if it starts before booking ends AND ends after booking starts
            return slotStart < bookingEnd && slotEnd > bookingStart;
        });
    }

    /**
     * Format date to YYYY-MM-DD string
     */
    formatDateToYYYYMMDD(date) {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    /**
     * Get calendar instance for external access
     */
    getCalendarInstance() {
        return this.calendar;
    }

    /**
     * Check if calendar is available on current page
     */
    static isAvailable() {
        return document.getElementById('fullCalendarAppointment') !== null;
    }
}
